import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { 
  Box, 
  CircularProgress,  
  IconButton,  
  Menu,  
  MenuItem,  
  Table, 
  TableBody, 
  TableCell, 
  TableFooter, 
  TableHead, 
  TablePagination, 
  TableRow, 
  TableSortLabel, 
  Typography 
} from '@mui/material';

import TablePaginationActions from '../../../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../../../components/hooks/useTablePaginationInit';
import useTableSortInit from '../../../../../../components/hooks/useTableSortInit';
import TableTimeStamps from '../../../../../../components/utils/TableTimeStamps';
import Copyable from '../../../../../../components/utils/Copyable';
import ConfirmationModal from '../../../../../../components/modals/ConfirmationModal';
import TopBar from '../../../../../../components/utils/TopBar';
import NA from '../../../../../../components/utils/NA';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import { 
  GetCCCountriesBIZcoreCC,
  SeedCCCountriesBIZcoreCC,
  WipeCCCountriesBIZcoreCC
} from '../../../../../../redux/actions/business/core/currencyCloudActions';

import CountriesModal from './CountriesModal';

import global from "../../../../../../global.scss";

const CountriesPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.core.cc"]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [countriesModalOpen, setCountriesModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [update, setUpdate] = useState(null);

  const [countriesList, setCountriesList] = useState([]);
  const [countriesTotal, setCountriesTotal] = useState(0);

  const [countriesMenuOpen, setCountriesMenuOpen] = useState(false);
  const [countriesMenuAnchor, setCountriesMenuAnchor] = useState(null);

  const [
    page, 
    rowsPerPage, 
    handleChangePage, 
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const [
    sort,
    sortDirection,
    handleSort
  ] = useTableSortInit();

  const [seedConfirmationModalOpen, setSeedConfirmationModalOpen] = useState(false);
  const [wipeConfirmationModalOpen, setWipeConfirmationModalOpen] = useState(false);

  // searching from a later page would land on an empty result set
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    handleChangePage(null, 0);
  };

  const tableHeadingsList = [
    {
      label: "Id",
      sort: "id",
    },
    {
      label: "Name",
      sort: "name",
    },
    {
      label: "Code",
      sort: "code",
    },
    {
      label: "Time stamps",
      sort: "created_at",
    },
    {
      label: "",
      sort: null
    }
  ];

  useEffect(() => {
    handleGetCountries();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      formatCountriesList(state.countriesList.records || []);
      setCountriesTotal(state.countriesList.total || 0);
    } catch (err) {}
  }, [state]);

  const formatCountriesList = (list) => {
    setCountriesList(list)
  };

  async function handleGetCountries(customLoading){
    let query = ""
    if(search) query += `&search=${search}`;
    if(sort) query += `&ordering=${sortDirection === "asc" ? '' : '-'}${sort}`;
    await dispatch(GetCCCountriesBIZcoreCC(page+1, rowsPerPage, query, customLoading ? customLoading : setLoading));
  };

  async function handleSeed(){
    setSeedConfirmationModalOpen(false);
    await dispatch(SeedCCCountriesBIZcoreCC(setLoading));
    handleGetCountries();
  };

  // NOTE: wired up and working, but intentionally left disabled in the UI —
  // these ids are FK targets for the payment/collection tables, so a wipe is
  // only ever appropriate on a throwaway environment. Re-enable deliberately.
  async function handleWipe(){
    setWipeConfirmationModalOpen(false);
    await dispatch(WipeCCCountriesBIZcoreCC(setLoading));
    handleGetCountries();
  };

  return (
    <Box>

      <CountriesModal 
        open={countriesModalOpen}
        setOpen={setCountriesModalOpen}
        title={modalTitle}
        update={update}
        handleGet={handleGetCountries}
        />

      <ConfirmationModal
        open={seedConfirmationModalOpen}
        setOpen={setSeedConfirmationModalOpen}
        title={"Seed the standard country list?"}
        message={"Inserts the 250 standard CurrencyCloud countries. Ids are pinned, so payment and collection references stay intact and rows already present are refreshed in place."}
        onConfirm={handleSeed}
        />

      <ConfirmationModal
        open={wipeConfirmationModalOpen}
        setOpen={setWipeConfirmationModalOpen}
        title={"Wipe all countries?"}
        message={"Permanently deletes every row in the CurrencyCloud countries table. The server refuses this while any payment or collection country still references it."}
        onConfirm={handleWipe}
        />

      <TopBar
        title={"Countries"}
        search={search}
        searchPlaceholder={"Search id, name or code"}
        onSearchChange={handleSearchChange}
        button2Text={"Seed all countries"}
        button2OnClick={() => {
          setSeedConfirmationModalOpen(true);
        }}
        button3Text={"Wipe all"}
        button3Disabled={true}
        button3OnClick={() => {
          setWipeConfirmationModalOpen(true);
        }}
        button1Text={"Add Country"}
        button1OnClick={() => {
          setUpdate({
            defaultMode: 1
          });
          setModalTitle("Add Country");
          setCountriesModalOpen(!countriesModalOpen);
        }}
        />

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          countriesList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No country found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell
                      key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell
                      key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort ? true : false}
                        direction={sortDirection}
                        onClick={() => {
                          handleSort(heading.sort);
                        }}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                countriesList.map((row, index) => (
                  <TableRow 
                    key={index}>
                    <TableCell>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row.id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>{row.name || <NA />}</TableCell>
                    <TableCell>{row.code || <NA />}</TableCell>
                    <TableCell>
                      <TableTimeStamps
                        createdAt={row.created_at}
                        updatedAt={row.updated_at}
                        />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={(e) => {
                          setCountriesMenuAnchor(e.currentTarget);
                          setCountriesMenuOpen(true);
                          setUpdate(row);
                          setModalTitle(row.name || "");
                        }}>
                        <MoreVertIcon sx={{ color: global['secondary_light']}}/>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[1, 5, 10, 25, 50, 100]} 
                  colSpan={10}
                  count={countriesTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page'
                    },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  />
              </TableRow>
            </TableFooter>
          </Table>
      }
      <Menu
        open={countriesMenuOpen}
        anchorEl={countriesMenuAnchor}
        onClose={() => setCountriesMenuOpen(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => {
            setModalTitle("Edit " + modalTitle);
            setUpdate({
              ...update,
              defaultMode: 2
            });
            setCountriesMenuOpen(false);
            setCountriesModalOpen(!countriesModalOpen);
          }}>
          <Box
            className="flexCenter_Row"
            gap={"10px"}>
            <EditOutlinedIcon />
            Edit
          </Box>
        </MenuItem>
      </Menu>
    </Box>
  )
}

export default CountriesPage;